import {
  CandidatesConst,
  CountryCodesConst,
  DefaultWeeklyHours,
  TaxonomyConst,
} from '@axiom/const';

export const extractEmailsBasedOnPreferences = ({
  preferredContactMethod,
  personalEmail,
  activeWorkEmail,
}) => {
  const normalizedContactMethod = (
    preferredContactMethod || 'none'
  ).toLowerCase();
  const emails = [];
  if (
    (normalizedContactMethod === 'both' ||
      normalizedContactMethod === 'personal') &&
    personalEmail
  ) {
    emails.push(personalEmail);
  }
  if (
    normalizedContactMethod === 'both' ||
    normalizedContactMethod === 'axiom'
  ) {
    if (activeWorkEmail) {
      emails.push(activeWorkEmail);
    } else if (!emails.length && personalEmail) {
      emails.push(personalEmail);
    }
  }
  return emails;
};

// Mirrors the constants in PreferredWeeklyHoursUtil
const PREFERRED_WEEKLY_HOURS_MAX = Object.freeze({
  CONTRACTOR_UK: 60,
  CONTRACTOR_GLOBAL: 30,
  LAWYER_AUSTRALIA: 40,
  LAWYER_GLOBAL: 60,
  ALP_CANADA: 44,
  ALP_UK_SG_HK: 60,
  ALP_REST_OF_WORLD: 40,
});

export const getPreferredWeeklyHoursMaximum = ({
  employeeType,
  businessTitle,
  addressCountryCode,
} = {}) => {
  const { UK, AU, CA, SG, HK } = CountryCodesConst.CountryCodesAbbreviations;

  if (employeeType === CandidatesConst.EmployeeTypes.Contractor) {
    return addressCountryCode === UK
      ? PREFERRED_WEEKLY_HOURS_MAX.CONTRACTOR_UK
      : PREFERRED_WEEKLY_HOURS_MAX.CONTRACTOR_GLOBAL;
  }

  if (businessTitle === TaxonomyConst.LawyerBusinessTitle) {
    return addressCountryCode === AU
      ? PREFERRED_WEEKLY_HOURS_MAX.LAWYER_AUSTRALIA
      : PREFERRED_WEEKLY_HOURS_MAX.LAWYER_GLOBAL;
  }

  if (addressCountryCode === CA) {
    return PREFERRED_WEEKLY_HOURS_MAX.ALP_CANADA;
  }

  if (
    addressCountryCode === UK ||
    addressCountryCode === SG ||
    addressCountryCode === HK
  ) {
    return PREFERRED_WEEKLY_HOURS_MAX.ALP_UK_SG_HK;
  }

  return PREFERRED_WEEKLY_HOURS_MAX.ALP_REST_OF_WORLD;
};

/**
 * Desired hours capped by the role/country maximum — the value availability is restored to on
 * a leave transition. Mirrors WeeklyAvailabilityUtil.restoredWeeklyAvailability.
 *
 * Callers must guard on desiredWeeklyMaxHours != null before calling, exactly as the Java
 * callers do. Math.min coerces a nullish desired into 0 or NaN rather than failing, so an
 * unguarded call silently stores a value that is not a real availability.
 */
export const desiredHoursWithinMaximum = (
  desiredWeeklyMaxHours,
  preferredWeeklyHoursMaximum
) => Math.min(desiredWeeklyMaxHours, preferredWeeklyHoursMaximum);

/**
 * The desired hours to store or report when the talent has stated no preference: the Workday
 * maxHours when we have one, otherwise a standard 40-hour week, in both cases capped by the
 * role/country maximum. Mirrors PreferredWeeklyHoursUtil.defaultDesiredWeeklyHours.
 *
 * The maximum is a ceiling, not a preference — defaulting straight to it would claim hours the
 * talent never asked for, so a 20-hour Workday week would be recorded as a 40- or 60-hour
 * preference and flow through weeklyAvailability into search.
 */
export const defaultDesiredWeeklyHours = (
  maxHours,
  preferredWeeklyHoursMaximum
) =>
  Math.min(
    maxHours ?? DefaultWeeklyHours.maxHours,
    preferredWeeklyHoursMaximum
  );
